import { useMemo, useState } from "react";
import { ComposedChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceDot } from "recharts";
import { useGet } from "@/lib/api";
import { fmt } from "@/lib/format";
import { GitCompareArrows } from "lucide-react";

const RANGES = [["4h", "4H"], ["1mo", "1M"], ["3mo", "3M"], ["6mo", "6M"], ["1y", "1Y · D"], ["1wk", "W · 5Y"]];
const MODES = [["rebased", "Rebased 100"], ["price", "Price"]];
const VERDICT = {
  holds: ["Inverse link holding", "#10B981"],
  weak: ["Inverse link weak", "#F59E0B"],
  "not holding": ["Moving together", "#EF4444"],
};
const day = (iso, rng) => {
  const dt = new Date(iso);
  if (rng === "4h") return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" });
  if (rng === "1wk") return dt.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

function Tip({ active, payload, rng }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2 text-[11px] font-mono-num">
      <div className="text-[#9CA3AF]">{day(p.date, rng)}</div>
      <div className="text-[#F59E0B]">Gold ${fmt(p.gold)} <span className="text-[#6B7280]">({p.gold_idx})</span></div>
      <div className="text-[#6366F1]">DXY {fmt(p.dxy)} <span className="text-[#6B7280]">({p.dxy_idx})</span></div>
    </div>
  );
}

export function DxyGoldChart({ refreshKey }) {
  const [range, setRange] = useState("3mo");
  const [mode, setMode] = useState("rebased");
  const { data: d, loading } = useGet(`/market-drivers/dxy-gold?range=${range}`, `${refreshKey}-${range}`);
  const [label, color] = VERDICT[d?.verdict] || ["", "#9CA3AF"];
  const lc = d?.last_cross;
  const rebased = mode === "rebased";
  // Price mode: give both axes the same % span so slopes are visually comparable across ranges
  const [goldDom, dxyDom] = useMemo(() => {
    if (!d?.series?.length) return [["auto", "auto"], ["auto", "auto"]];
    const span = (k) => { const v = d.series.map((p) => p[k]); const lo = Math.min(...v), hi = Math.max(...v); return [lo, hi, (hi - lo) / ((lo + hi) / 2)]; };
    const [glo, ghi, gs] = span("gold"); const [dlo, dhi, ds] = span("dxy");
    const pct = Math.max(gs, ds) * 1.1;
    const dom = (lo, hi) => { const mid = (lo + hi) / 2; return [+(mid * (1 - pct / 2)).toFixed(2), +(mid * (1 + pct / 2)).toFixed(2)]; };
    return [dom(glo, ghi), dom(dlo, dhi)];
  }, [d]);

  return (
    <section data-testid="dxy-gold-card" className="glass rounded-3xl p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold flex items-center gap-2"><GitCompareArrows size={15} className="text-[#F59E0B]" /> Gold vs US Dollar (DXY)</div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{rebased ? "Both rebased to 100 at the start of the window" : "Gold left, DXY right · both axes span the same % so slopes are comparable"} · {d?.label || "hypothesis: dollar up → gold down"}</div>
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="dxy-gold-range">
          {MODES.map(([v, l]) => (
            <button key={v} data-testid={`dxy-gold-mode-${v}`} onClick={() => setMode(v)}
              className={`px-2.5 h-7 rounded-lg text-[11px] font-mono-num font-semibold border transition-colors ${mode === v ? "bg-white/15 text-white border-white/20" : "border-white/10 text-[#9CA3AF] hover:bg-white/10"}`}>{l}</button>
          ))}
          <span className="w-px bg-white/10 mx-1" />
          {RANGES.map(([v, l]) => (
            <button key={v} data-testid={`dxy-gold-range-${v}`} onClick={() => setRange(v)}
              className={`px-2.5 h-7 rounded-lg text-[11px] font-mono-num font-semibold border transition-colors ${range === v ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-white/10 text-[#9CA3AF] hover:bg-white/10"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading && !d ? <div className="h-56 grid place-items-center text-xs text-[#6B7280] font-mono-num">Loading…</div> : d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <Stat label="Verdict" value={label} color={color} testId="dxy-gold-verdict" />
            <Stat label="Last crossing" testId="dxy-gold-last-cross"
              color={lc ? (lc.direction === "gold_above" ? "#10B981" : "#EF4444") : "#9CA3AF"}
              value={lc ? <>Gold {lc.direction === "gold_above" ? "above" : "below"} DXY{lc.forming ? " (today, forming)" : ""}<br /><span className="text-[#9CA3AF]">{day(lc.date, range)} · {lc.direction === "gold_above" ? "BUY" : "SELL"} bias</span></> : "none in window"} />
            <Stat label="Opposite days" value={d.opposite_pct != null ? `${d.opposite_pct}%` : "—"} color={d.opposite_pct >= 55 ? "#10B981" : "#9CA3AF"} testId="dxy-gold-opposite" />
            <Stat label="Correlation" value={d.correlation} color={d.correlation <= -0.3 ? "#10B981" : d.correlation < 0 ? "#F59E0B" : "#EF4444"} testId="dxy-gold-corr" />
            <Stat label="Significance" testId="dxy-gold-significance"
              color={d.significant_0_01pct ? "#10B981" : d.significant_at ? "#F59E0B" : "#EF4444"}
              value={d.correlation >= 0 ? "n/a" : d.significant_at ? <>at {d.significant_at} level<br /><span className="text-[#9CA3AF]">{d.p_value < 0.0001 ? "p < 0.0001" : `p = ${d.p_value.toPrecision(2)}`}</span></> : <>not significant<br /><span className="text-[#9CA3AF]">p = {d.p_value.toPrecision(2)}</span></>} />
            <Stat label={`Change · ${RANGES.find((r) => r[0] === range)[1]}`}
              value={<><span className="text-[#F59E0B]">Gold {d.gold_change_pct > 0 ? "+" : ""}{d.gold_change_pct}%</span><br /><span className="text-[#6366F1]">DXY {d.dxy_change_pct > 0 ? "+" : ""}{d.dxy_change_pct}%</span></>} testId="dxy-gold-change" />
          </div>
          <div className="h-56 sm:h-64 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" tickFormatter={(v) => day(v, range)} tick={{ fill: "#6B7280", fontSize: 10 }} minTickGap={36} axisLine={false} tickLine={false} />
                {rebased ? (
                  <YAxis yAxisId="idx" domain={["auto", "auto"]} tick={{ fill: "#9CA3AF", fontSize: 10 }} width={40} axisLine={false} tickLine={false} />
                ) : (
                  <>
                    <YAxis yAxisId="gold" domain={goldDom} tick={{ fill: "#F59E0B", fontSize: 10 }} width={52} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(Math.round(v))} />
                    <YAxis yAxisId="dxy" orientation="right" domain={dxyDom} tick={{ fill: "#818CF8", fontSize: 10 }} width={40} axisLine={false} tickLine={false} tickFormatter={(v) => v.toFixed(1)} />
                  </>
                )}
                <Tooltip content={<Tip rng={range} />} cursor={{ stroke: "#ffffff22" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="font-mono-num text-[#9CA3AF]">{v}</span>} />
                <Line yAxisId={rebased ? "idx" : "gold"} type="monotone" dataKey={rebased ? "gold_idx" : "gold"} name={rebased ? "Gold (rebased 100)" : "Gold (XAUUSD, left $)"} stroke="#F59E0B" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line yAxisId={rebased ? "idx" : "dxy"} type="monotone" dataKey={rebased ? "dxy_idx" : "dxy"} name={rebased ? "US Dollar Index (rebased 100)" : "US Dollar Index (DXY, right)"} stroke="#6366F1" strokeWidth={2} dot={false} isAnimationActive={false} />
                {rebased && (d.crossings || []).map((c) => (
                  <ReferenceDot key={c.date} yAxisId="idx" x={c.date} y={d.series.find((p) => p.date === c.date)?.gold_idx} r={5}
                    fill={c.direction === "gold_above" ? "#10B981" : "#EF4444"} stroke="#0B0F17" strokeWidth={1.5} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p data-testid="dxy-gold-summary" className="text-[11px] text-[#9CA3AF] leading-relaxed">{d.summary}</p>
          {rebased && (
            <p data-testid="dxy-gold-cross-text" className="text-[11px] leading-relaxed text-[#D1D5DB]">
              <span className="text-[#FB923C] font-semibold">Position-trade crossings:</span> both lines start at 100, so when the gold line crosses above the dollar line gold is out-performing (BUY bias); below means the dollar is winning (SELL bias).{" "}
              {d.crossings?.length ? `${d.crossings.length} crossing${d.crossings.length > 1 ? "s" : ""} in this window — dots on the chart. ` : "No crossing in this window. "}
              {lc && `Latest: gold ${lc.direction === "gold_above" ? "above" : "below"} on ${day(lc.date, range)} (gold $${fmt(lc.gold)} / DXY ${fmt(lc.dxy)})${lc.forming ? " — today's bar is still forming, so this only counts once the day closes" : ""}. `}
              Both lines are rebased at the start of the window, so older crossings can shift as the window rolls forward; the long-term position alert uses the 4H · 60-day chart and only counts completed 4H bars.
            </p>
          )}
          <p data-testid="dxy-gold-significance-text" className="text-[11px] leading-relaxed" style={{ color: d.significant_0_01pct ? "#10B981" : d.significant_at ? "#F59E0B" : "#9CA3AF" }}>
            {d.significance_text}. {d.significant_0_01pct
              ? "In plain terms: there is less than a 1-in-10,000 chance of seeing a link this strong if gold and the dollar were truly unrelated."
              : d.significant_at ? `Strong evidence, but not quite at the 0.01% bar — a longer window (6M/1Y) usually gets there.` : ""}
          </p>
        </>
      )}
    </section>
  );
}

function Stat({ label, value, color = "#D1D5DB", testId }) {
  return (
    <div data-testid={testId} className="glass rounded-xl px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="text-xs font-mono-num font-semibold leading-snug" style={{ color }}>{value}</div>
    </div>
  );
}
